import type CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import checkDirtyStateThenShowNotification from '../utils/checkDirtyStateThenShowNotification';
import EventScope from 'owa-service/lib/contract/EventScope';
import generateEventDiff from '../utils/generateEventDiff';
import getMailTipsViewStateMap from '../utils/getMailTipsViewStateMap';
import { getComposeStore } from 'owa-calendar-compose-store-manager';
import { logDiscardActionMailTipsDatapoint } from 'owa-mail-tips/lib/datapoints';
import logEventMetricProperties from '../utils/logEventMetricProperties';
import {
    deleteCalendarEvent,
    deleteCalendarEventService,
    removeBirthdayEventService,
} from 'owa-calendar-events-actions';
import {
    EVENT_COMPLETION_STATUS_DISCARDED,
    QuickComposeEntrySourceType,
    FullComposeEntrySourceType,
} from 'owa-calendar-helpers-types/lib/ComposeMetricTypes';
import {
    isSeriesInstance,
    isBirthdayEvent,
    isNewEventDraftWithAttachments,
} from 'owa-calendar-event-capabilities';
import { orchestrator } from 'satcheljs';
import { PerformanceDatapoint } from 'owa-analytics';
import { userInteractionAction } from 'owa-calendar-actions';
import './showUndoNotificationForCompose';
import {
    closeComposeForm,
    discardEvent,
    logComposeFormOpenedTimes,
    discardAddedAttachments,
} from 'owa-calendar-compose-form-actions';
import isInPopOutWindow from 'owa-calendar-compose-form-store/lib/selectors/isInPopOutWindow';
import { getItemIdToUpdate } from 'owa-calendar-events-store';
import { lazyDiscardTravelBookends } from 'owa-mobility-compose';
import { isFeatureEnabled } from 'owa-feature-flags';

export default orchestrator(discardEvent, async actionMessage => {
    const { event } = getComposeStore();
    const datapoint = new PerformanceDatapoint('DiscardCalendarEvent');

    logDiscardActionMailTipsDatapoint(getMailTipsViewStateMap(event));

    if (actionMessage.shouldShowNotification) {
        checkDirtyStateThenShowNotification();
    }

    if (isNewEventDraftWithAttachments(event)) {
        // If it is a draft or new event with itemid (attachment scenario), then delete the calendar event
        const eventScopeToDelete = isSeriesInstance(event)
            ? EventScope.ThisInstanceOnly
            : EventScope.Default;

        if (isInPopOutWindow()) {
            // If it is full compose in pop out, then we need to call the delete service directly and await the response before closing the window
            const itemIdToDelete = getItemIdToUpdate(event.ItemId.Id, eventScopeToDelete);
            if (isBirthdayEvent(event)) {
                // If event is a birthday event, then we use another service to delete
                await removeBirthdayEventService(itemIdToDelete, event.ItemId.mailboxInfo);
            } else {
                await deleteCalendarEventService(
                    itemIdToDelete,
                    eventScopeToDelete,
                    event.ItemId.mailboxInfo
                );
            }
        } else {
            // If it's not full compose in pop out, then just call deleteCalendarEvent action which updates the cache and asynchronously calls delete
            deleteCalendarEvent(event, 'EditForm', eventScopeToDelete);
        }
    } else {
        // Only discard attachments if it's not a new/draft scenario
        discardAddedAttachments();
    }

    // Log the user interaction action
    userInteractionAction('Discard', 'EditForm', event.IsOrganizer, event.IsMeeting);

    if (isFeatureEnabled('cal-mf-mobility')) {
        lazyDiscardTravelBookends.importAndExecute(event, 'CalendarCompose_Discard');
    }

    logMetrics(datapoint);
    closeComposeForm(
        false /*shouldResetComposeStoreForUndo*/,
        event.IsMeeting,
        actionMessage.shouldShowNotification /*shouldClosePopOutWindow*/
    );
    datapoint.end();
});

function logMetrics(datapoint) {
    const { event, originalEvent, metricsInfo, openedForm, uiState } = getComposeStore();
    const quickComposeEntrySource: QuickComposeEntrySourceType =
        metricsInfo.quickComposeEntrySource;
    const fullComposeEntrySource: FullComposeEntrySourceType = metricsInfo.fullComposeEntrySource;
    const diffEvent: CalendarEvent = generateEventDiff(event, originalEvent);

    let quickComposeDiffEvent: CalendarEvent = null;
    let fullComposeDiffEvent: CalendarEvent = null;
    if (openedForm === 'FullCompose' && fullComposeEntrySource === 'QuickCompose') {
        const quickComposeFinalEvent = metricsInfo.quickComposeFinalEvent;
        quickComposeDiffEvent = generateEventDiff(quickComposeFinalEvent, originalEvent);
        fullComposeDiffEvent = generateEventDiff(event, quickComposeFinalEvent);
    }

    logComposeFormOpenedTimes();

    logEventMetricProperties(
        datapoint,
        diffEvent,
        quickComposeDiffEvent,
        fullComposeDiffEvent,
        event,
        EVENT_COMPLETION_STATUS_DISCARDED,
        openedForm,
        uiState?.viewType,
        quickComposeEntrySource,
        metricsInfo,
        fullComposeEntrySource,
        originalEvent
    );
}
